import { Alert, InputNumber, Space, Typography } from 'antd';
import BigNumber from 'bignumber.js';
import React from 'react';
import { NumericKeyboardInput } from '@/components/NumericKeyboard';
import PosButton from '@/components/PosButton';
import { Pay_Consume_Limit_Types, PointRules } from '@/models/point';
import { User } from '@/models/user';
import { playSound } from '@/utils/sound';

/**
 * 积分转抵扣金额（纯换算）
 * @param point 积分数
 * @param pointRules 积分规则
 */
export const pointToAmount = (point: number, pointRules: PointRules) => {
  if (!point || !pointRules) {
    return new BigNumber(0);
  }
  return new BigNumber(point).dividedBy(pointRules.payConsumeUnit).multipliedBy(pointRules.payConsumeAmount);
};

/**
 * 积分抵扣计算（含钳制）：返回抵扣金额与修正后的积分数量。
 * 积分抵扣不能超过基准价（应付不为负）；上下文变化导致基准价变小（如重新选卡）时，
 * 反向折算基准价内可用的最大积分数并同步修正 pointNum，保持 pointNum ↔ pointPrice 一致。
 * @param pointNum 用户使用的积分数
 * @param pointRules 积分规则
 * @param maxPointPrice 卡券抵扣后的基准价（积分抵扣上限）
 */
export const calcPointConsume = (
  pointNum: number,
  pointRules: PointRules,
  maxPointPrice: BigNumber
): { pointPrice: BigNumber; revisedPointNum: number } => {
  let pointPrice = pointToAmount(pointNum, pointRules);
  let revisedPointNum = pointNum;
  if (pointRules && pointPrice.gt(maxPointPrice)) {
    const maxPointNum = maxPointPrice
      .dividedBy(pointRules.payConsumeAmount)
      .multipliedBy(pointRules.payConsumeUnit)
      .integerValue(BigNumber.ROUND_FLOOR)
      .toNumber();
    revisedPointNum = Math.max(0, maxPointNum);
    pointPrice = pointToAmount(revisedPointNum, pointRules);
  }
  return { pointPrice, revisedPointNum };
};

type Props = {
  user: User;
  pointRules: PointRules;
  price: BigNumber;
  // 使用积分
  value?: number;
  onChange?: (value: number) => void;
};

const PointConsume: React.FC<Props> = ({ user, pointRules, price, value, onChange }: Props) => {
  if (!user) {
    return <Alert type="warning" showIcon message="无客户信息" />;
  }

  if (!pointRules) {
    return <Alert type="warning" showIcon message="积分规则未配置" />;
  }

  if (!pointRules?.payConsumeOn) {
    return <Alert type="warning" showIcon message="积分抵现未开启" />;
  }

  // 每分钱消耗积分数
  let perCentConsumePoint = new BigNumber(pointRules.payConsumeUnit).dividedBy(
    new BigNumber(pointRules.payConsumeAmount).multipliedBy(100)
  );

  // 价格完全抵扣所需积分
  let maxLimitConsumePointBigNum = price.multipliedBy(100).multipliedBy(perCentConsumePoint);

  if (pointRules.payConsumeLimitType === Pay_Consume_Limit_Types.FIXED) {
    // min(价格完全抵扣所需积分 , 每单最多使用积分)
    maxLimitConsumePointBigNum = BigNumber.min(pointRules.payConsumeLimitNum, maxLimitConsumePointBigNum);
  } else if (pointRules.payConsumeLimitType === Pay_Consume_Limit_Types.RATIO) {
    maxLimitConsumePointBigNum = BigNumber.min(
      maxLimitConsumePointBigNum,
      // 每单抵扣金额不超过支付金额的 {payConsumeLimitNum}%
      maxLimitConsumePointBigNum.multipliedBy(new BigNumber(pointRules.payConsumeLimitNum).dividedBy(100))
    );
  }

  // 不超过客户持有积分数
  const maxLimitConsumePoint = BigNumber.min(user.point, maxLimitConsumePointBigNum)
    .integerValue(BigNumber.ROUND_FLOOR)
    .toNumber();

  return (
    <>
      <Space size={10} direction={'vertical'}>
        <div style={{ fontSize: 14 }}>
          用户积分: <Typography.Text code>{user.point}</Typography.Text>，本单最多可使用{' '}
          <Typography.Text code>{maxLimitConsumePoint}</Typography.Text> 分{/*用户积分: {user.point}, 总价值{' '}*/}
          {/*<Price*/}
          {/*  value={new BigNumber(user.point)*/}
          {/*    .dividedBy(pointRules.payConsumeUnit)*/}
          {/*    .multipliedBy(pointRules.payConsumeAmount)*/}
          {/*    .toNumber()}*/}
          {/*/>*/}
        </div>
        {maxLimitConsumePoint > 0 && (
          <div>
            <Space size={10}>
              {/*<InputNumber*/}
              {/*  size={'middle'}*/}
              {/*  value={value}*/}
              {/*  min={0}*/}
              {/*  max={maxLimitConsumePoint}*/}
              {/*  precision={0}*/}
              {/*  placeholder={'请输入该订单使用积分数'}*/}
              {/*  style={{ width: 200 }}*/}
              {/*  onChange={v => {*/}
              {/*    if (!v) {*/}
              {/*      onChange(undefined);*/}
              {/*    } else {*/}
              {/*      onChange(+v);*/}
              {/*    }*/}
              {/*  }}*/}
              {/*  onPressEnter={e => {*/}
              {/*    e.preventDefault();*/}
              {/*  }}*/}
              {/*/>*/}

              <NumericKeyboardInput
                value={value}
                inputType={InputNumber}
                onChange={v => {
                  if (!v) {
                    onChange(undefined);
                  } else {
                    if (+v > maxLimitConsumePoint) {
                      onChange(maxLimitConsumePoint);
                    } else {
                      onChange(+v);
                    }
                  }
                }}
                inputProps={{
                  placeholder: '抵扣积分数',
                  style: { width: '100%' },
                  min: 0,
                  max: maxLimitConsumePoint,
                  precision: 0,
                }}
                keyboardProps={{
                  parseValueToNumber: true,
                  precision: 2,
                  showAllClean: true,
                  placement: 'right',
                  trigger: 'click',
                  style: { width: 300 },
                }}
              />
              <PosButton
                onClick={() => {
                  onChange(maxLimitConsumePoint);
                  playSound('click');
                }}>
                全部使用
              </PosButton>
              <PosButton
                onClick={() => {
                  onChange(undefined);
                  playSound('click');
                }}>
                重置
              </PosButton>
            </Space>
          </div>
        )}
      </Space>
    </>
  );
};

export default PointConsume;
